var _excluded = ["children", "dismissOnBackdropClick", "firstFocusableElement", "isOpen", "onCancel", "visibleBackdrop"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Backdrop, Wrapper } from "../Modal/styledComponents.js";
import { closeOnEscape, returnFocusToActivator, setFocus, trapFocus } from "../Modal/utils.js";
import { DRAWER_BODY_CLASSNAME, DRAWER_BODY_ID } from "./DrawerBody.js";
import { DRAWER_FOOTER_CLASSNAME } from "./DrawerFooter.js";
import { DRAWER_HEADER_CLASSNAME, DRAWER_HEADER_ID } from "./DrawerHeader.js";
var drawerClassNames = {
  body: DRAWER_BODY_CLASSNAME,
  footer: DRAWER_FOOTER_CLASSNAME,
  header: DRAWER_HEADER_CLASSNAME
};
var StyledContainer = styled(motion.div).withConfig({
  componentId: "CL__sc-1hxkhdf-0"
})(["position:absolute;right:0;top:0;width:412px;overflow:auto;height:100vh;padding:0 var(--spacing-3);background-color:var(--modal-bkg-color);box-shadow:var(--box-shadow-small);"]);
var StyledScroller = styled.div.withConfig({
  componentId: "CL__sc-1hxkhdf-1"
})(["position:relative;"]);
var StyledSentinel = styled.div.withConfig({
  componentId: "CL__sc-1hxkhdf-2"
})(["position:absolute;left:0;width:0;height:0;background:transparent;opacity:0;"]);
var Drawer = _ref => {
  var {
      children,
      dismissOnBackdropClick = true,
      firstFocusableElement,
      isOpen,
      onCancel,
      visibleBackdrop = false
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var drawerRef = useRef(null);
  var [activator, setActivator] = useState(null);
  var [isAtTop, setIsAtTop] = useState(true);
  var [isAtBottom, setIsAtBottom] = useState(false);
  var [shouldScroll, setShouldScroll] = useState(false);

  // Handle focus and keyboard events
  useEffect(() => {
    if (!isOpen) {
      returnFocusToActivator(activator);
      return;
    }
    setActivator(document.activeElement);
    setFocus(drawerRef, drawerClassNames, firstFocusableElement);
    var eventHandlers = event => {
      trapFocus(event, drawerRef);
      closeOnEscape(event, onCancel);
    };
    window.addEventListener('keydown', eventHandlers);
    return () => {
      window.removeEventListener('keydown', eventHandlers);
    };
  }, [isOpen]);

  // Is the drawer content long enough to need scrolling?
  var topSentinel = useRef(null);
  var bottomSentinel = useRef(null);
  useEffect(() => {
    if (!shouldScroll) return;
    var drawer = drawerRef.current;
    var top = topSentinel.current;
    var bottom = bottomSentinel.current;
    if (!drawer || !top || !bottom) return;
    var observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === top) {
          setIsAtTop(entry.isIntersecting);
        } else if (entry.target === bottom) {
          setIsAtBottom(entry.isIntersecting);
        }
      });
    }, {
      root: drawer,
      threshold: 0.01 // small value = triggers as soon as it peeks in
    });
    observer.observe(top);
    observer.observe(bottom);
    return () => {
      observer.disconnect();
    };
  }, [shouldScroll]);

  // Check for changes on resize or mutation
  useEffect(() => {
    var drawer = drawerRef.current;
    if (!drawer) return;
    var checkOverflow = () => {
      var needsScroll = drawer.scrollHeight > drawer.clientHeight;
      setShouldScroll(needsScroll);
    };
    checkOverflow();
    var resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(drawer);
    var mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(drawer, {
      childList: true,
      subtree: true
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isOpen]);
  return /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(AnimatePresence, null, isOpen && /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Backdrop, {
    key: "backdrop",
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 'var(--modal-backdrop-opacity)'
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    },
    "data-visible": visibleBackdrop,
    onClick: dismissOnBackdropClick ? onCancel : undefined
  }), /*#__PURE__*/React.createElement(StyledContainer, _extends({
    className: "".concat(shouldScroll && 'is-scrollable', " ").concat(isAtTop && 'is-at-top', " ").concat(isAtBottom && 'is-at-bottom'),
    key: "drawer-container",
    initial: {
      x: '50%',
      opacity: 0
    },
    animate: {
      x: '0%',
      opacity: 1
    },
    transition: {
      duration: 0.3,
      ease: 'easeInOut'
    },
    ref: drawerRef,
    "aria-labelledby": DRAWER_HEADER_ID,
    "aria-describedby": DRAWER_BODY_ID,
    "aria-modal": "true",
    role: "dialog"
  }, props), /*#__PURE__*/React.createElement(StyledScroller, null, /*#__PURE__*/React.createElement(StyledSentinel, {
    ref: topSentinel,
    style: {
      top: 0
    }
  }), children, /*#__PURE__*/React.createElement(StyledSentinel, {
    ref: bottomSentinel,
    style: {
      bottom: 0
    }
  }))))), document.body);
};
export default Drawer;